import React, { useEffect, useState } from 'react';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { BaseSelect } from '@app/components/common/selects/BaseSelect/BaseSelect';
import { Button, notification, Form, Col, Row, DatePicker, Upload, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useTranslation } from 'react-i18next';
import { createPost, showPost, editPost, storePost, updatePost, uploadPostImage } from '@app/api/post.api'; // Added uploadPostImage

interface PostFormProps {
  pageTitle: string;
  editable: boolean;
  id: string | null;
  type: 'edit' | 'create' | 'view';
  // eslint-disable-next-line @typescript-eslint/ban-types
  setCurrentBreadcrumName: Function;
}

const PostForm: React.FC<PostFormProps> = ({ editable = false, id, pageTitle, type, setCurrentBreadcrumName }) => {
  const [form] = Form.useForm();
  const [isLoading, setLoading] = useState(false);
  const [statusOptions, setStatusOptions] = useState<{ label: string; value: string }[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<{ value: number; label: string }[]>([]);
  const [imageFile, setImageFile] = useState<string | null>(null);
  const [imageFiles, setImageFiles] = useState<string[]>([]);
  const { t } = useTranslation();
  const { addHeaderButton, removeHeaderButton } = useHeader();
  const navigate = useNavigate();
  const postId = parseInt(id ?? '0');

  const setApiData = (res: any) => {
    setCurrentBreadcrumName(res?.post?.title || 'New');
    form.setFieldsValue({
      title: res?.post?.title,
      slug: res?.post?.slug,
      body: res?.post?.body,
      display_date: res?.post?.display_date ? dayjs(res?.post?.display_date) : null,
      published_on: res?.post?.published_on ? dayjs(res?.post?.published_on) : null,
      post_category_id: res?.post?.post_category_id,
      status: res?.post?.status,
    });
    if (res?.post?.images && res?.post?.images.length > 0) {
      const urls: string[] = res.post.images.map((imgObj: any) => {
        const raw = imgObj.image;

        try {
          // Check if it's a JSON stringified array
          if (raw.startsWith('["')) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
              return parsed[0];
            }
          }
        } catch (err) {
          console.warn('Failed to parse image:', raw);
        }

        // Otherwise return as-is
        return raw.replace(/\\\//g, '/');
      });

      setImageFiles(urls);
    }
  };

  const setOptions = (res: any) => {
    setStatusOptions(
      res.status_options?.map((opt: string) => ({
        label: opt.charAt(0).toUpperCase() + opt.slice(1),
        value: opt,
      })) || [],
    );

    setCategoryOptions(
      res.category_options?.map((cat: any) => ({
        label: cat.label,
        value: cat.value,
      })) || [],
    );
  };

  const getPost = () => {
    setLoading(true);
    if (postId) {
      if (type === 'view') {
        showPost(postId)
          .then((res: any) => {
            setApiData(res);
            setLoading(false);
          })
          .catch((err: any) => {
            setLoading(false);
            notification.error({ message: 'Failed to get post', description: err.message });
          });
      } else if (type === 'edit') {
        editPost(postId)
          .then((res: any) => {
            setApiData(res);
            setOptions(res);
            setLoading(false);
          })
          .catch((err: any) => {
            setLoading(false);
            notification.error({ message: 'Failed to edit post', description: err.message });
          });
      }
    } else if (type === 'create') {
      createPost()
        .then((res: any) => {
          setOptions(res);
          setLoading(false);
        })
        .catch((err: any) => {
          setLoading(false);
          notification.error({ message: 'Failed to fetch post data', description: err.message });
        });
    }
  };

  useEffect(() => {
    if (editable) {
      addHeaderButton(
        'save-button',
        <BaseButton type="primary" htmlType="submit" loading={isLoading} onClick={() => form.submit()} size="small">
          {t('Save')}
        </BaseButton>,
      );

      addHeaderButton(
        'cancel-button',
        <BaseButton onClick={() => navigate('/posts')} style={{ marginRight: '8px' }} size="small">
          {t('Cancel')}
        </BaseButton>,
      );
    }

    if (!editable && id) {
      addHeaderButton(
        'edit-button',
        <BaseButton type="primary" onClick={() => navigate(`/edit-post`, { state: { id } })} size="small">
          {t('Edit')}
        </BaseButton>,
      );
    }

    return () => {
      removeHeaderButton('save-button');
      removeHeaderButton('cancel-button');
      removeHeaderButton('edit-button');
    };
  }, [form, isLoading, editable, id, addHeaderButton, t, navigate, removeHeaderButton]);

  useEffect(() => {
    getPost();
  }, [id, setCurrentBreadcrumName]);

  const handleImageUpload = async (fileList: File[]) => {
    try {
      const result = await uploadPostImage(fileList);
      const uploadedUrls = result
        .map((fileObj: any) => {
          let url = fileObj?.url ? `${process.env.REACT_APP_BASE_URL}${fileObj.url}` : null;

          if (url) {
            url = url.replace(/\\\//g, '/').replace(/\\/g, '');
            if (url.includes('/api')) {
              url = url.replace('/api', '');
            }
          }

          return url;
        })
        .filter(Boolean);

      setImageFiles((prev) => [...prev, ...uploadedUrls]);
      message.success('Images uploaded successfully');
    } catch (error) {
      message.error('Image upload failed!');
    }
  };

  const handleSubmit = (values: any) => {
    console.log('Form Values:', values);
    console.log('Image URL:', imageFile); // Log the image URL as well

    const payload = {
      ...values,
      display_date: values.display_date?.format('YYYY-MM-DD') ?? null,
      published_on: values.published_on?.format('YYYY-MM-DD') ?? null,
      image: imageFiles.length > 0 ? imageFiles : null,
      body: values.body || '',
    };

    console.log('Payload:', payload); // Log the final payload

    setLoading(true);

    if (type === 'create') {
      storePost(payload)
        .then((res: any) => {
          setLoading(false);
          navigate('/view-post', { state: { id: res?.data?.id } });
        })
        .catch((err: any) => {
          setLoading(false);
          notification.error({ message: err.message });
        });
    } else if (type === 'edit' && postId) {
      updatePost(postId, payload)
        .then(() => {
          setLoading(false);
          navigate('/view-post', { state: { id: postId } });
        })
        .catch((err: any) => {
          setLoading(false);
          notification.error({ message: err.message });
        });
    }
  };

  return (
    <BaseRow gutter={[16, 16]} style={{ margin: '0 8px' }}>
      <BaseCol span={24}>
        <BaseCard id="postForm" title={pageTitle} padding="1.25rem">
          <BaseRow gutter={[16, 16]}>
            <BaseCol span={24}>
              <Form
                form={form}
                name="postForm"
                labelCol={{ lg: { span: 6 }, md: { span: 6 }, sm: { span: 24 } }}
                labelAlign="left"
                labelWrap
                wrapperCol={{ lg: { span: 16 }, md: { span: 18 }, sm: { span: 24 } }}
                size="small"
                onFinish={handleSubmit}
                style={{ marginLeft: '40px' }}
              >
                <Row gutter={16}>
                  <Col xs={24} md={24} lg={12}>
                    <Form.Item name="title" label="Title" rules={[{ required: true }]}>
                      {type === 'view' ? (
                        <BaseInput value={form.getFieldValue('title')} readOnly />
                      ) : (
                        <BaseInput placeholder="Enter Title" readOnly={!editable} />
                      )}
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={24} lg={12}>
                    <Form.Item name="slug" label="Slug" rules={[{ required: true }]}>
                      {type === 'view' ? (
                        <BaseInput value={form.getFieldValue('slug')} readOnly />
                      ) : (
                        <BaseInput placeholder="Enter Slug" readOnly={!editable} />
                      )}
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={24} lg={12}>
                    <Form.Item name="post_category_id" label="Category" rules={[{ required: true }]}>
                      {type === 'view' ? (
                        <BaseInput
                          value={
                            categoryOptions.find((option) => option.value === form.getFieldValue('post_category_id'))
                              ?.label
                          }
                          readOnly
                        />
                      ) : (
                        <BaseSelect placeholder="Select Category" options={categoryOptions} disabled={!editable} />
                      )}
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={24} lg={12}>
                    <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                      {type === 'view' ? (
                        <BaseInput
                          value={statusOptions.find((option) => option.value === form.getFieldValue('status'))?.label}
                          readOnly
                        />
                      ) : (
                        <BaseSelect placeholder="Select Status" options={statusOptions} disabled={!editable} />
                      )}
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={24} lg={12}>
                    <Form.Item name="display_date" label="Display Date">
                      {type === 'view' ? (
                        <BaseInput value={form.getFieldValue('display_date')?.format('YYYY-MM-DD')} readOnly />
                      ) : (
                        <DatePicker style={{ width: '100%' }} disabled={!editable} />
                      )}
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={24} lg={12}>
                    <Form.Item name="published_on" label="Published On">
                      {type === 'view' ? (
                        <BaseInput value={form.getFieldValue('published_on')?.format('YYYY-MM-DD')} readOnly />
                      ) : (
                        <DatePicker style={{ width: '100%' }} disabled={!editable} />
                      )}
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item name="body" label="Body" rules={[{ required: false }]}>
                      {type === 'view' ? (
                        <BaseInput.TextArea value={form.getFieldValue('body')} readOnly rows={6} />
                      ) : (
                        <BaseInput.TextArea rows={6} placeholder="Enter Post Content" disabled={!editable} />
                      )}
                    </Form.Item>
                  </Col>
                </Row>

                {/* Image Upload Section */}
                <Row gutter={16}>
                  <Col span={24}>
                    {/* Image Upload Section */}
                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item label="Images">
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
                            {imageFiles.length > 0 ? (
                              imageFiles.map((url, index) => (
                                <img
                                  key={index}
                                  src={url}
                                  alt={`Post Image ${index}`}
                                  style={{
                                    width: '150px',
                                    height: 'auto',
                                    objectFit: 'cover',
                                    borderRadius: '8px',
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                  }}
                                />
                              ))
                            ) : (
                              <span>No images available</span>
                            )}
                          </div>

                          {(type === 'edit' || type === 'create') && (
                            <Upload
                              multiple
                              beforeUpload={(file) => {
                                handleImageUpload([file]);
                                return false;
                              }}
                              showUploadList={false}
                            >
                              <Button>Click to Upload</Button>
                            </Upload>
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Form>
            </BaseCol>
          </BaseRow>
        </BaseCard>
      </BaseCol>
    </BaseRow>
  );
};

export default PostForm;
